import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  where, 
  serverTimestamp,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useCallback, useEffect, useState } from 'react';
import { Worksheet, CreateWorksheetData, WorksheetSignature } from '../types/worksheet';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';

export const useWorksheets = () => {
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { canAdmin, isSuperAdmin } = usePermissions();

  // Generate worksheet number
  const generateWorksheetNumber = useCallback(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0');
    return `WS-${year}${month}${day}-${time}`;
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const worksheetsRef = collection(db, 'worksheets');
    let q = query(worksheetsRef, orderBy('createdAt', 'desc'));

    // A super_admin minden munkalapot láthat
    // Más szerepkörök esetén nincs előzetes szűrés, mert a munkalapoknál
    // az organizationId opcionális, így a betöltés után szűrjük
    // ha szükséges a jogosultságok alapján

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        try {
          const loadedWorksheets = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              dueDate: data.dueDate?.toDate() || null,
              worksheetDate: data.worksheetDate?.toDate() || null,
              deploymentDate: data.deploymentDate?.toDate() || null,
              completedAt: data.completedAt?.toDate() || null,
              signatures: data.signatures?.map((sig: any) => ({
                ...sig,
                signedAt: sig.signedAt?.toDate() || new Date()
              })) || []
            } as Worksheet;
          });

          // Szűrés jogosultságok alapján
          let filteredWorksheets = loadedWorksheets;
          
          // Ha a felhasználó nem super_admin és nem admin a worksheets modulban,
          // és van organizationId, akkor szűrünk
          if (!isSuperAdmin && !canAdmin('worksheets') && user?.organizationId) {
            filteredWorksheets = loadedWorksheets.filter(worksheet => 
              worksheet.organizationId === user.organizationId
            );
          }

          setWorksheets(filteredWorksheets);
          setError(null);
          setLoading(false);
        } catch (err) {
          console.error('Error processing worksheets:', err);
          setError(err as Error);
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error loading worksheets:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, isSuperAdmin, canAdmin]);

  const addWorksheet = useCallback(async (worksheetData: CreateWorksheetData) => {
    try {
      const worksheetsRef = collection(db, 'worksheets');
      const worksheetNumber = generateWorksheetNumber();
      
      const newWorksheet = {
        ...worksheetData,
        worksheetNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || '',
        organizationId: user?.organizationId || '',
        signatures: [],
        documents: []
      };

      const docRef = await addDoc(worksheetsRef, newWorksheet);
      return docRef.id;
    } catch (err) {
      console.error('Error adding worksheet:', err);
      throw err;
    }
  }, [user, generateWorksheetNumber]);

  const updateWorksheet = useCallback(async (id: string, updateData: Partial<CreateWorksheetData>) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      const dataToUpdate: any = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      if (updateData.dueDate) {
        dataToUpdate.dueDate = Timestamp.fromDate(updateData.dueDate);
      }

      await updateDoc(worksheetRef, dataToUpdate);
    } catch (err) {
      console.error('Error updating worksheet:', err);
      throw err;
    }
  }, []);

  const deleteWorksheet = useCallback(async (id: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      await deleteDoc(worksheetRef);
    } catch (err) {
      console.error('Error deleting worksheet:', err);
      throw err;
    }
  }, []);

  const addSignature = useCallback(async (worksheetId: string, signature: Omit<WorksheetSignature, 'id'>) => {
    try {
      const worksheetRef = doc(db, 'worksheets', worksheetId);
      const worksheet = worksheets.find(w => w.id === worksheetId);
      
      if (!worksheet) {
        throw new Error('Worksheet not found');
      }

      const newSignature = {
        ...signature,
        id: `sig_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        signedAt: Timestamp.fromDate(new Date()) // Use Timestamp.fromDate instead of serverTimestamp
      };

      const updatedSignatures = [...(worksheet.signatures || []), newSignature];
      
      await updateDoc(worksheetRef, {
        signatures: updatedSignatures,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding signature:', err);
      throw err;
    }
  }, [worksheets]);

  const removeSignature = useCallback(async (worksheetId: string, signatureId: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', worksheetId);
      const worksheet = worksheets.find(w => w.id === worksheetId);
      
      if (!worksheet) {
        throw new Error('Worksheet not found');
      }

      const updatedSignatures = worksheet.signatures?.filter(sig => sig.id !== signatureId) || [];
      
      await updateDoc(worksheetRef, {
        signatures: updatedSignatures,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error removing signature:', err);
      throw err;
    }
  }, [worksheets]);

  const completeWorksheet = useCallback(async (id: string) => {
    try {
      const worksheetRef = doc(db, 'worksheets', id);
      await updateDoc(worksheetRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: user?.uid || '',
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error completing worksheet:', err);
      throw err;
    }
  }, [user]);

  return {
    worksheets,
    loading,
    error,
    addWorksheet,
    updateWorksheet,
    deleteWorksheet,
    addSignature,
    removeSignature,
    completeWorksheet
  };
}; 